import { cn } from 'lib/utils'
import { CalendarIcon, ChevronDown, X } from 'lucide-react'
import { Button } from 'primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'
import { Separator } from 'primitives/separator'
import { Switch } from 'primitives/switch'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  Column,
  ColumnDataType,
  DataTableFilterActions,
  FilterModel,
  FilterStrategy,
  FiltersState,
} from '../core/types'
import type { Locale } from '../lib/i18n'
import { FilterOperator } from './filter-operator'
import { FilterSubject } from './filter-subject'
import { FilterValueController, FilterValueDisplay } from './filter-value'

// A filter chip's label is a column display name, not a fixed action label, so it
// opts out of the Button's global mono `label` token (primitives/button.tsx) and
// reads as Inter — matching the value half, which was already forced back.
//
// It is also `ghost`, not `outline`: `outline` carries brandHoverGlint, whose
// `hover:text-brand-foreground` (white) is NOT cancelled by the chip's own
// `hover:bg-accent/50` — twMerge only dedupes the background — so an applied chip
// hovered to white-on-near-white. A filter chip is a control you operate, not a
// brand action, so ghost's quiet hover is the right treatment and the shell
// (hairline border, surface) comes from CHIP_SHELL_CLASS instead of the variant.
const CHIP_TYPE_CLASS = 'font-inter text-s-400 normal-case tracking-normal'
const CHIP_SHELL_CLASS = 'h-[30px] max-w-full rounded-md border border-st2 bg-background'

interface ActiveFiltersProps<TData> {
  columns: Column<TData>[]
  filters: FiltersState
  actions: DataTableFilterActions
  strategy: FilterStrategy
  locale?: Locale
}

export function ActiveFilters<TData>({
  columns,
  filters,
  actions,
  strategy,
  locale = 'en',
}: ActiveFiltersProps<TData>) {
  // Memoize ordered columns to avoid recalculating on every render
  const orderedColumns = useMemo(() => {
    // Split columns into regular and "showInMoreFilters" columns
    const regularColumns = columns.filter((col) => !col.showInMoreFilters)
    const moreFilterColumns = columns.filter((col) => col.showInMoreFilters)

    // For "showInMoreFilters" columns, sort by the order they appear in filters array
    const sortedMoreFilterColumns = moreFilterColumns
      .map((column) => {
        const filter = filters.find((f) => f.columnId === column.id)
        const hasValue = filter?.values && filter.values.length > 0
        const filterIndex = filter ? filters.indexOf(filter) : -1
        return { column, hasValue, filterIndex }
      })
      .filter(({ hasValue }) => hasValue) // Only show applied filters
      .sort((a, b) => a.filterIndex - b.filterIndex) // Sort by application order
      .map(({ column }) => column)

    // Combine: regular columns first (in original order), then more filters (in applied order)
    return [...regularColumns, ...sortedMoreFilterColumns]
  }, [columns, filters])

  return (
    <>
      {orderedColumns.map((column) => {
        const filter = filters.find((f) => f.columnId === column.id)

        return (
          <ActiveFilterWrapper
            key={`active-filter-${column.id}`}
            filter={filter}
            column={column}
            actions={actions}
            strategy={strategy}
            locale={locale}
          />
        )
      })}
    </>
  )
}

interface BooleanFilterProps<TData> {
  column: Column<TData, 'boolean'>
  filter: FilterModel<'boolean'> | undefined
  actions: DataTableFilterActions
}

function BooleanFilter<TData>({ column, filter, actions }: BooleanFilterProps<TData>) {
  const value = filter?.values?.[0] ?? false

  const handleChange = useCallback(
    (checked: boolean) => {
      actions.setFilterValue(column, [checked])
    },
    [actions, column],
  )

  return (
    <Switch
      label={
        <span className="font-inter text-s-500 normal-case tracking-normal">
          {column.displayName}
        </span>
      }
      value={value}
      onChange={handleChange}
      className="h-[30px] px-3 border border-st2 rounded-md bg-background"
    />
  )
}

interface ActiveFilterWrapperProps<TData> {
  filter: FilterModel<ColumnDataType> | undefined
  column: Column<TData>
  actions: DataTableFilterActions
  strategy: FilterStrategy
  locale?: Locale
}

function ActiveFilterWrapper<TData>({
  filter,
  column,
  actions,
  strategy,
  locale = 'en',
}: ActiveFilterWrapperProps<TData>): JSX.Element {
  const [open, setOpen] = useState(false)
  const hasValue = filter?.values && filter.values.length > 0

  // Close popover when filter is cleared (becomes empty)
  useEffect(() => {
    if (!hasValue) {
      setOpen(false)
    }
  }, [hasValue])

  // Memoize the remove handler to prevent recreating on every render
  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      actions.removeFilter(filter!.columnId)
      setOpen(false)
    },
    [actions, filter],
  )

  // Memoize icon based on column type
  const emptyFilterIcon = useMemo(
    () => (column.type === 'date' ? <CalendarIcon className="size-4" /> : null),
    [column.type],
  )

  // Boolean filters don't need a popup
  if (column.type === 'boolean') {
    return (
      <BooleanFilter
        column={column as Column<TData, 'boolean'>}
        filter={filter as FilterModel<'boolean'> | undefined}
        actions={actions}
      />
    )
  }

  // For text and number types without values, don't show anything
  if (!hasValue && (column.type === 'text' || column.type === 'number')) {
    return <></>
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {!hasValue ? (
          <Button
            variant="ghost"
            // px-2.5, not the Button default px-4: the chip is a compact filter
            // slot, and Inter needs less breathing room than the mono label did.
            className={cn(CHIP_SHELL_CLASS, CHIP_TYPE_CLASS, 'gap-1.5 px-2.5')}
          >
            {emptyFilterIcon}
            <span>{column.displayName}</span>
            <ChevronDown className="size-3.5 text-tertiary" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            className={cn(
              CHIP_SHELL_CLASS,
              CHIP_TYPE_CLASS,
              'flex max-w-full items-center cursor-pointer hover:bg-accent/50 transition-colors p-0 gap-0',
            )}
          >
            <FilterSubject column={column} />
            <Separator orientation="vertical" className="bg-hv1" />
            <FilterOperator filter={filter} column={column} actions={actions} locale={locale} />
            <Separator orientation="vertical" className="bg-hv1" />
            <div className="px-2 shrink min-w-0 flex">
              <FilterValueDisplay
                filter={filter}
                column={column as Column<TData, ColumnDataType>}
                actions={actions}
                locale={locale}
              />
            </div>
            {/* Required/always-set filters (canClear === false) hide the X so they can't be
                removed individually — the chip has no clear affordance. */}
            {column.canClear !== false && (
              <>
                <Separator orientation="vertical" className="bg-hv1" />
                <Button
                  variant="ghost"
                  size="iconXs"
                  className="rounded-none rounded-r-md w-7 h-full shrink-0"
                  onClick={handleRemove}
                >
                  <X className="size-4 text-muted-foreground" />
                </Button>
              </>
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className={cn(
          'w-fit p-0 origin-(--radix-popover-content-transform-origin)',
          column.type !== 'date' && 'max-w-96',
        )}
      >
        <FilterValueController
          filter={filter}
          column={column as Column<TData, ColumnDataType>}
          actions={actions}
          strategy={strategy}
          locale={locale}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}

export function ActiveFiltersMobileContainer({ children }: { children: React.ReactNode }) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftBlur, setShowLeftBlur] = useState(false)
  const [showRightBlur, setShowRightBlur] = useState(true)

  const checkScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current

      setShowLeftBlur(scrollLeft > 0)

      setShowRightBlur(scrollLeft + clientWidth < scrollWidth - 1)
    }
  }, [])

  useEffect(() => {
    if (!scrollContainerRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      checkScroll()
    })
    resizeObserver.observe(scrollContainerRef.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [checkScroll])

  useEffect(() => {
    checkScroll()
  }, [children, checkScroll])

  return (
    <div className="relative w-full overflow-x-hidden">
      {showLeftBlur && (
        <div className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-r from-background to-transparent animate-in fade-in-0" />
      )}

      <div
        ref={scrollContainerRef}
        className="flex gap-2 overflow-x-scroll no-scrollbar"
        onScroll={checkScroll}
      >
        {children}
      </div>

      {showRightBlur && (
        <div className="absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-l from-background to-transparent animate-in fade-in-0 " />
      )}
    </div>
  )
}
